// Equipment definitions with tier system (T1-T4)
// Post-overhaul model:
//   - Unified `power` cost per (tier, size) — see POWER_COST table.
//   - No more ENGINE / SHIELD equipment (movement and shield are now native to the ship).
//   - Weapons unified to 3 families × 4 tiers = 12 items (Kinetic / Energy / Explosive).
//   - Mining tools unified to 4 items (one family).
//   - Enhancers expanded with Heat Dissipator + Shield Amplifier.
//   - Utilities trimmed (Warp Disruptor / Cargo Scanner removed).

import { SlotType, SlotSize } from './ships.js';

export type EquipmentTier = 1 | 2 | 3 | 4;

// ENGINE / SHIELD categories kept as valid enum values for backward-compat with
// consumer code that switches on the union, but no items of those categories exist.
export type EquipmentCategory = 'ENGINE' | 'WEAPON' | 'TOOL' | 'SHIELD' | 'ENHANCER' | 'UTILITY';

export interface EquipmentBase {
  id: string;
  name: string;
  description: string;
  category: EquipmentCategory;
  slotType: SlotType;
  size: SlotSize;
  tier: EquipmentTier;
  value: number;
  volume: number;
  power: number;
}

// ============================================
// POWER COST TABLE
// power = base(tier) × size_multiplier
//   SMALL  ×1.0
//   MEDIUM ×1.5  (rounded)
//   LARGE  ×2.5  (rounded)
// ============================================
export const POWER_BASE_PER_TIER: Record<EquipmentTier, number> = {
  1: 15,
  2: 35,
  3: 70,
  4: 140,
};

export function powerCost(tier: EquipmentTier, size: SlotSize): number {
  const base = POWER_BASE_PER_TIER[tier];
  switch (size) {
    case 'SMALL':  return base;
    case 'MEDIUM': return Math.round(base * 1.5);
    case 'LARGE':  return Math.round(base * 2.5);
  }
}

// ============================================
// ENGINE — kept as empty registry for backward-compat.
// Movement is native to the ship. Use Thruster Booster (enhancer) to amplify.
// ============================================
export interface EngineStats {
  accelerationBonus: number;
  maxSpeedBonus: number;
  accelerationMultiplier: number;
  maxSpeedMultiplier: number;
  strafeBonus: number;
  strafeMultiplier: number;
}

export interface Engine extends EquipmentBase {
  category: 'ENGINE';
  slotType: 'ENGINE';
  engineType: 'proton' | 'ion' | 'plasma';
  stats: EngineStats;
}

export const ENGINES: Record<string, Engine> = {};

// ============================================
// SHIELD — kept as empty registry for backward-compat.
// Shields are native to the ship. Use Shield Amplifier (enhancer) to expand radius.
// ============================================
export interface ShieldStats {
  shieldCapacity: number;
  rechargeRate: number;
  rechargeDelay: number;
}

export interface Shield extends EquipmentBase {
  category: 'SHIELD';
  slotType: 'SHIELD';
  shieldType: 'deflector' | 'absorber' | 'regenerator';
  stats: ShieldStats;
}

export const SHIELDS: Record<string, Shield> = {};

// ============================================
// WEAPONS
// 3 families × 4 tiers = 12 items
//   Kinetic   — SMALL    — vs shield ×1.5 / vs hull ×0.75  (ballistic, no tracking)
//   Energy    — MEDIUM   — vs shield ×1.0 / vs hull ×1.0   (continuous beam, ramp-up)
//   Explosive — MEDIUM   — vs shield ×0.75 / vs hull ×1.5  (homing missile, no magazine — heat only)
// ============================================

export interface DamageMultiplier {
  shield: number;
  hull: number;
}

export interface WeaponStats {
  damage: number;
  fireRate: number;            // shots per second (0 for continuous beams)
  range: number;
  projectileSpeed: number;     // 0 for hitscan beams
  heatPerShot: number;         // generated per shot (0 for beams — use beamHeatRate)
  projectileColor: number;
  projectileGlow: number;

  // damage type multiplier (against shield vs hull)
  damageMultiplier: DamageMultiplier;

  // Beam-only (Energy weapons)
  beamDamageRampUp?: number;     // bonus dmg multiplier added per second on target
  maxDamageMultiplier?: number;  // cap on ramp-up
  beamHeatRate?: number;         // heat generated per second while firing

  // Missile-only (Explosive weapons) — NO magazine, NO reload, heat is the only limiter
  trackingSpeed?: number;        // degrees/sec turn rate
  lifetime?: number;             // ms until self-destruct
  trackingRange?: number;        // units where tracking is active
  missileHp?: number;            // destructible warhead, ordinary weapon damage

  // Legacy / back-compat fields (kept so older consumers compile).
  // The new model uses ship-level heat capacity / dissipation; these are derived
  // sensible defaults so per-weapon heat sims still behave.
  energyCost?: number;
  cooldownRate?: number;          // legacy: heat dissipated per second
  overheatThreshold?: number;     // legacy: 100
  overheatCooldown?: number;      // legacy: ms forced cooldown when overheated
  magazineSize?: number;          // legacy: explosive magazine (no longer used)
  reloadTime?: number;            // legacy
}

// Canonical weapon types in the new model are 'kinetic' | 'energy' | 'explosive'.
// 'laser' and 'missile' are kept as legacy aliases so old client code keeps compiling.
// Treat them as synonyms: laser ≡ energy, missile ≡ explosive.
export type WeaponType = 'kinetic' | 'energy' | 'explosive' | 'laser' | 'missile';

export interface Weapon extends EquipmentBase {
  category: 'WEAPON';
  slotType: 'WEAPON';
  weaponType: WeaponType;
  stats: WeaponStats;
}

const KINETIC_MULT: DamageMultiplier = { shield: 1.5, hull: 0.75 };
const ENERGY_MULT:  DamageMultiplier = { shield: 1.0, hull: 1.0 };
const EXPLOSIVE_MULT: DamageMultiplier = { shield: 0.75, hull: 1.5 };

export const WEAPONS: Record<string, Weapon> = {
  // === KINETIC (SMALL, ballistic, anti-shield) ===
  weapon_kinetic_t1: {
    id: 'weapon_kinetic_t1', name: 'Kinetic T1',
    description: 'Ballistic autocannon. Strong vs shields, weak vs hull.',
    category: 'WEAPON', slotType: 'WEAPON', size: 'SMALL', tier: 1,
    value: 350, volume: 6, power: powerCost(1, 'SMALL'),
    weaponType: 'kinetic',
    stats: {
      damage: 12, fireRate: 3.0, range: 350, projectileSpeed: 300,
      heatPerShot: 10, damageMultiplier: KINETIC_MULT,
      projectileColor: 0xffaa00, projectileGlow: 0xffcc44,
    },
  },
  weapon_kinetic_t2: {
    id: 'weapon_kinetic_t2', name: 'Kinetic T2',
    description: 'Higher caliber rounds. Punches harder vs shields.',
    category: 'WEAPON', slotType: 'WEAPON', size: 'SMALL', tier: 2,
    value: 1400, volume: 8, power: powerCost(2, 'SMALL'),
    weaponType: 'kinetic',
    stats: {
      damage: 20, fireRate: 3.5, range: 400, projectileSpeed: 325,
      heatPerShot: 12, damageMultiplier: KINETIC_MULT,
      projectileColor: 0xffaa00, projectileGlow: 0xffcc44,
    },
  },
  weapon_kinetic_t3: {
    id: 'weapon_kinetic_t3', name: 'Kinetic T3',
    description: 'Railgun-class projectiles. Devastating shield damage.',
    category: 'WEAPON', slotType: 'WEAPON', size: 'SMALL', tier: 3,
    value: 6500, volume: 12, power: powerCost(3, 'SMALL'),
    weaponType: 'kinetic',
    stats: {
      damage: 35, fireRate: 2.5, range: 500, projectileSpeed: 375,
      heatPerShot: 16, damageMultiplier: KINETIC_MULT,
      projectileColor: 0xffbb00, projectileGlow: 0xffdd55,
    },
  },
  weapon_kinetic_t4: {
    id: 'weapon_kinetic_t4', name: 'Kinetic T4',
    description: 'Capital-grade kinetic. Shreds shields outright.',
    category: 'WEAPON', slotType: 'WEAPON', size: 'SMALL', tier: 4,
    value: 20000, volume: 16, power: powerCost(4, 'SMALL'),
    weaponType: 'kinetic',
    stats: {
      damage: 55, fireRate: 2.0, range: 600, projectileSpeed: 425,
      heatPerShot: 20, damageMultiplier: KINETIC_MULT,
      projectileColor: 0xffcc00, projectileGlow: 0xffee66,
    },
  },

  // === ENERGY (MEDIUM, beam, balanced) ===
  weapon_energy_t1: {
    id: 'weapon_energy_t1', name: 'Energy T1',
    description: 'Continuous beam. Damage ramps up while on target.',
    category: 'WEAPON', slotType: 'WEAPON', size: 'MEDIUM', tier: 1,
    value: 400, volume: 4, power: powerCost(1, 'MEDIUM'),
    weaponType: 'energy',
    stats: {
      damage: 5, fireRate: 0, range: 400, projectileSpeed: 0,
      heatPerShot: 0, damageMultiplier: ENERGY_MULT,
      projectileColor: 0xff0000, projectileGlow: 0xff4444,
      beamDamageRampUp: 0.3, maxDamageMultiplier: 2.0, beamHeatRate: 14,
    },
  },
  weapon_energy_t2: {
    id: 'weapon_energy_t2', name: 'Energy T2',
    description: 'Tighter beam focus. Faster ramp-up.',
    category: 'WEAPON', slotType: 'WEAPON', size: 'MEDIUM', tier: 2,
    value: 1600, volume: 5, power: powerCost(2, 'MEDIUM'),
    weaponType: 'energy',
    stats: {
      damage: 8, fireRate: 0, range: 450, projectileSpeed: 0,
      heatPerShot: 0, damageMultiplier: ENERGY_MULT,
      projectileColor: 0xff2200, projectileGlow: 0xff5544,
      beamDamageRampUp: 0.4, maxDamageMultiplier: 2.5, beamHeatRate: 18,
    },
  },
  weapon_energy_t3: {
    id: 'weapon_energy_t3', name: 'Energy T3',
    description: 'High-output beam. Lethal when sustained.',
    category: 'WEAPON', slotType: 'WEAPON', size: 'MEDIUM', tier: 3,
    value: 7000, volume: 7, power: powerCost(3, 'MEDIUM'),
    weaponType: 'energy',
    stats: {
      damage: 12, fireRate: 0, range: 500, projectileSpeed: 0,
      heatPerShot: 0, damageMultiplier: ENERGY_MULT,
      projectileColor: 0xff3300, projectileGlow: 0xff6644,
      beamDamageRampUp: 0.5, maxDamageMultiplier: 3.0, beamHeatRate: 22,
    },
  },
  weapon_energy_t4: {
    id: 'weapon_energy_t4', name: 'Energy T4',
    description: 'Military-grade beam. Maximum sustained damage.',
    category: 'WEAPON', slotType: 'WEAPON', size: 'MEDIUM', tier: 4,
    value: 22000, volume: 10, power: powerCost(4, 'MEDIUM'),
    weaponType: 'energy',
    stats: {
      damage: 18, fireRate: 0, range: 550, projectileSpeed: 0,
      heatPerShot: 0, damageMultiplier: ENERGY_MULT,
      projectileColor: 0xff4400, projectileGlow: 0xff7755,
      beamDamageRampUp: 0.6, maxDamageMultiplier: 3.5, beamHeatRate: 28,
    },
  },

  // === EXPLOSIVE (MEDIUM, homing, anti-hull, heat-only) ===
  weapon_explosive_t1: {
    id: 'weapon_explosive_t1', name: 'Explosive T1',
    description: 'Tracking missile. Strong vs hull, weak vs shields.',
    category: 'WEAPON', slotType: 'WEAPON', size: 'MEDIUM', tier: 1,
    value: 500, volume: 8, power: powerCost(1, 'MEDIUM'),
    weaponType: 'explosive',
    stats: {
      damage: 25, fireRate: 1.0, range: 600, projectileSpeed: 150,
      heatPerShot: 25, damageMultiplier: EXPLOSIVE_MULT,
      projectileColor: 0x00aaff, projectileGlow: 0x44ccff,
      trackingSpeed: 150, lifetime: 5000, trackingRange: 600, missileHp: 12,
    },
  },
  weapon_explosive_t2: {
    id: 'weapon_explosive_t2', name: 'Explosive T2',
    description: 'Improved warheads and tracking.',
    category: 'WEAPON', slotType: 'WEAPON', size: 'MEDIUM', tier: 2,
    value: 2000, volume: 10, power: powerCost(2, 'MEDIUM'),
    weaponType: 'explosive',
    stats: {
      damage: 40, fireRate: 1.2, range: 700, projectileSpeed: 175,
      heatPerShot: 24, damageMultiplier: EXPLOSIVE_MULT,
      projectileColor: 0x00aaff, projectileGlow: 0x44ccff,
      trackingSpeed: 180, lifetime: 6000, trackingRange: 700, missileHp: 18,
    },
  },
  weapon_explosive_t3: {
    id: 'weapon_explosive_t3', name: 'Explosive T3',
    description: 'Heavy torpedo. Aggressive tracking.',
    category: 'WEAPON', slotType: 'WEAPON', size: 'MEDIUM', tier: 3,
    value: 8500, volume: 14, power: powerCost(3, 'MEDIUM'),
    weaponType: 'explosive',
    stats: {
      damage: 65, fireRate: 1.0, range: 800, projectileSpeed: 200,
      heatPerShot: 30, damageMultiplier: EXPLOSIVE_MULT,
      projectileColor: 0x00bbff, projectileGlow: 0x55ddff,
      trackingSpeed: 210, lifetime: 7000, trackingRange: 800, missileHp: 24,
    },
  },
  weapon_explosive_t4: {
    id: 'weapon_explosive_t4', name: 'Explosive T4',
    description: 'Capital torpedo. Relentless pursuit.',
    category: 'WEAPON', slotType: 'WEAPON', size: 'MEDIUM', tier: 4,
    value: 26000, volume: 20, power: powerCost(4, 'MEDIUM'),
    weaponType: 'explosive',
    stats: {
      damage: 100, fireRate: 0.8, range: 900, projectileSpeed: 225,
      heatPerShot: 38, damageMultiplier: EXPLOSIVE_MULT,
      projectileColor: 0x00ccff, projectileGlow: 0x66eeff,
      trackingSpeed: 240, lifetime: 8000, trackingRange: 900, missileHp: 36,
    },
  },
};

// ============================================
// MINING TOOLS — single family × 4 tiers
// ============================================
export interface MiningToolStats {
  miningSpeed: number;      // multiplier; higher = faster
  efficiency: number;       // multiplier for yield
  range: number;
  energyCost: number;       // legacy field (not consumed in new model)
  miningPower: number;      // base damage per mining tick (consumed by server mining loop)
}

// Legacy union — kept so existing consumers that branch on `toolType` compile.
export type ToolType = 'laser_drill' | 'plasma_cutter' | 'sonic_drill';

export interface MiningTool extends EquipmentBase {
  category: 'TOOL';
  slotType: 'TOOL';
  toolType: ToolType;       // always 'laser_drill' in new model
  stats: MiningToolStats;
}

export const MINING_TOOLS: Record<string, MiningTool> = {
  tool_mining_t1: {
    id: 'tool_mining_t1', name: 'Mining Tool T1',
    description: 'Basic mining laser. Mines all resource types.',
    category: 'TOOL', slotType: 'TOOL', size: 'SMALL', tier: 1,
    value: 300, volume: 5, power: powerCost(1, 'SMALL'),
    toolType: 'laser_drill',
    stats: { miningSpeed: 1.0, efficiency: 1.0, range: 560, energyCost: 4, miningPower: 12 },
  },
  tool_mining_t2: {
    id: 'tool_mining_t2', name: 'Mining Tool T2',
    description: 'Improved mining laser. Faster, more efficient.',
    category: 'TOOL', slotType: 'TOOL', size: 'SMALL', tier: 2,
    value: 1200, volume: 6, power: powerCost(2, 'SMALL'),
    toolType: 'laser_drill',
    stats: { miningSpeed: 1.2, efficiency: 1.15, range: 700, energyCost: 6, miningPower: 14.4 },
  },
  tool_mining_t3: {
    id: 'tool_mining_t3', name: 'Mining Tool T3',
    description: 'High-output mining laser.',
    category: 'TOOL', slotType: 'TOOL', size: 'SMALL', tier: 3,
    value: 5500, volume: 8, power: powerCost(3, 'SMALL'),
    toolType: 'laser_drill',
    stats: { miningSpeed: 1.4, efficiency: 1.25, range: 840, energyCost: 9, miningPower: 19.2 },
  },
  tool_mining_t4: {
    id: 'tool_mining_t4', name: 'Mining Tool T4',
    description: 'Top-tier mining laser.',
    category: 'TOOL', slotType: 'TOOL', size: 'SMALL', tier: 4,
    value: 18000, volume: 10, power: powerCost(4, 'SMALL'),
    toolType: 'laser_drill',
    stats: { miningSpeed: 1.6, efficiency: 1.35, range: 980, energyCost: 14, miningPower: 24 },
  },
};

// ============================================
// ENHANCERS (passive — set in fitting screen, no in-game UI)
// 6 families × 4 tiers = 24 items
// ============================================

export type EnhancerType =
  | 'hull_plating'
  | 'cargo_expander'
  | 'sensor_array'
  | 'booster'
  | 'heat_dissipator'
  | 'shield_amplifier';

export interface EquipmentModStats {
  // Hull / Cargo / Sensor
  hullBonus?: number;
  cargoBonus?: number;
  sensorBonus?: number;

  // Booster (thruster)
  boostSpeedMultiplier?: number;
  boostDuration?: number;
  boostCooldown?: number;

  // Sensor Array — radar scan thresholds (hold-R)
  radarScanTimes?: number[];
  radarScanRanges?: number[];

  // Heat Dissipator
  heatCapacityBonus?: number;     // flat heat capacity added
  heatDissipationBonus?: number;  // flat heat-dissipation/sec added

  // Shield Amplifier
  shieldRadiusBonus?: number;     // adds to native shield radius
  shieldCoversAllies?: boolean;   // allies inside the field also benefit

  // Drone Bay (utility)
  droneCapacity?: number;
  droneZoiRadius?: number;

  // Beacon Deployer (utility)
  beaconTier?: number;
  beaconDeployCooldown?: number;

  // Legacy fields (kept so older client code compiles — no items use them now)
  warpDisruptRange?: number;
  warpDisruptDuration?: number;
  warpDisruptCooldown?: number;
  scanRange?: number;
  scanDuration?: number;
  scanCooldown?: number;
}

export type UtilityStats = EquipmentModStats; // backward-compat alias

export interface Enhancer extends EquipmentBase {
  category: 'ENHANCER';
  slotType: 'ENHANCER';
  enhancerType: EnhancerType;
  stats: EquipmentModStats;
}

function enhancer(
  id: string, name: string, description: string,
  enhancerType: EnhancerType, size: SlotSize, tier: EquipmentTier,
  value: number, volume: number, stats: EquipmentModStats,
): Enhancer {
  return {
    id, name, description,
    category: 'ENHANCER', slotType: 'ENHANCER',
    size, tier, value, volume,
    power: powerCost(tier, size),
    enhancerType, stats,
  };
}

export const ENHANCERS: Record<string, Enhancer> = {
  // === HULL PLATING (MEDIUM, +hull) ===
  enhancer_hull_t1: enhancer('enhancer_hull_t1', 'Hull Plating T1',
    'Basic armor plating.', 'hull_plating', 'MEDIUM', 1, 400, 10, { hullBonus: 20 }),
  enhancer_hull_t2: enhancer('enhancer_hull_t2', 'Hull Plating T2',
    'Reinforced armor.', 'hull_plating', 'MEDIUM', 2, 1600, 15, { hullBonus: 50 }),
  enhancer_hull_t3: enhancer('enhancer_hull_t3', 'Hull Plating T3',
    'Composite armor.', 'hull_plating', 'MEDIUM', 3, 6500, 20, { hullBonus: 90 }),
  enhancer_hull_t4: enhancer('enhancer_hull_t4', 'Hull Plating T4',
    'Capital-grade armor.', 'hull_plating', 'MEDIUM', 4, 22000, 25, { hullBonus: 150 }),

  // === CARGO EXPANDER (SMALL, +cargo) ===
  enhancer_cargo_t1: enhancer('enhancer_cargo_t1', 'Cargo Expander T1',
    'Extra cargo capacity.', 'cargo_expander', 'SMALL', 1, 300, 5, { cargoBonus: 10 }),
  enhancer_cargo_t2: enhancer('enhancer_cargo_t2', 'Cargo Expander T2',
    'More cargo capacity.', 'cargo_expander', 'SMALL', 2, 1200, 8, { cargoBonus: 25 }),
  enhancer_cargo_t3: enhancer('enhancer_cargo_t3', 'Cargo Expander T3',
    'Significant cargo expansion.', 'cargo_expander', 'SMALL', 3, 5000, 12, { cargoBonus: 45 }),
  enhancer_cargo_t4: enhancer('enhancer_cargo_t4', 'Cargo Expander T4',
    'Maximum cargo expansion.', 'cargo_expander', 'SMALL', 4, 17000, 16, { cargoBonus: 75 }),

  // === SENSOR ARRAY (SMALL, +radar scan / target scan via R) ===
  enhancer_sensor_t1: enhancer('enhancer_sensor_t1', 'Sensor Array T1',
    'Basic radar enhancement.', 'sensor_array', 'SMALL', 1, 350, 4, {
      sensorBonus: 150,
      radarScanTimes: [0.5, 1.5, 3.0],
      radarScanRanges: [11250, 19800, 28125],
    }),
  enhancer_sensor_t2: enhancer('enhancer_sensor_t2', 'Sensor Array T2',
    'Improved radar.', 'sensor_array', 'SMALL', 2, 1400, 6, {
      sensorBonus: 300,
      radarScanTimes: [0.4, 1.2, 2.5],
      radarScanRanges: [15300, 26550, 39375],
    }),
  enhancer_sensor_t3: enhancer('enhancer_sensor_t3', 'Sensor Array T3',
    'Long-range radar.', 'sensor_array', 'SMALL', 3, 5500, 9, {
      sensorBonus: 550,
      radarScanTimes: [0.3, 1.0, 2.0],
      radarScanRanges: [21150, 36450, 53550],
    }),
  enhancer_sensor_t4: enhancer('enhancer_sensor_t4', 'Sensor Array T4',
    'Capital-grade radar.', 'sensor_array', 'SMALL', 4, 18000, 12, {
      sensorBonus: 800,
      radarScanTimes: [0.25, 0.8, 1.5],
      radarScanRanges: [28125, 47700, 67500],
    }),

  // === THRUSTER BOOSTER (MEDIUM, amplifies native boost) ===
  enhancer_booster_t1: enhancer('enhancer_booster_t1', 'Thruster Booster T1',
    'Improves the native boost.', 'booster', 'MEDIUM', 1, 400, 8, {
      boostSpeedMultiplier: 1.4, boostDuration: 1500, boostCooldown: 8000,
    }),
  enhancer_booster_t2: enhancer('enhancer_booster_t2', 'Thruster Booster T2',
    'Stronger boost.', 'booster', 'MEDIUM', 2, 1600, 10, {
      boostSpeedMultiplier: 1.55, boostDuration: 2000, boostCooldown: 7000,
    }),
  enhancer_booster_t3: enhancer('enhancer_booster_t3', 'Thruster Booster T3',
    'High-output boost.', 'booster', 'MEDIUM', 3, 6500, 14, {
      boostSpeedMultiplier: 1.7, boostDuration: 2500, boostCooldown: 6000,
    }),
  enhancer_booster_t4: enhancer('enhancer_booster_t4', 'Thruster Booster T4',
    'Capital-grade boost.', 'booster', 'MEDIUM', 4, 22000, 18, {
      boostSpeedMultiplier: 1.8, boostDuration: 3000, boostCooldown: 5000,
    }),

  // === HEAT DISSIPATOR (MEDIUM, +heat cap & +dissipation) ===
  enhancer_heat_t1: enhancer('enhancer_heat_t1', 'Heat Dissipator T1',
    'Improves heat handling.', 'heat_dissipator', 'MEDIUM', 1, 450, 8, {
      heatCapacityBonus: 15, heatDissipationBonus: 2,
    }),
  enhancer_heat_t2: enhancer('enhancer_heat_t2', 'Heat Dissipator T2',
    'Better heat exchangers.', 'heat_dissipator', 'MEDIUM', 2, 1800, 11, {
      heatCapacityBonus: 30, heatDissipationBonus: 4,
    }),
  enhancer_heat_t3: enhancer('enhancer_heat_t3', 'Heat Dissipator T3',
    'High-efficiency dissipators.', 'heat_dissipator', 'MEDIUM', 3, 7200, 15, {
      heatCapacityBonus: 50, heatDissipationBonus: 6,
    }),
  enhancer_heat_t4: enhancer('enhancer_heat_t4', 'Heat Dissipator T4',
    'Capital-grade thermal management.', 'heat_dissipator', 'MEDIUM', 4, 24000, 20, {
      heatCapacityBonus: 80, heatDissipationBonus: 9,
    }),

  // === SHIELD AMPLIFIER (LARGE, expands shield radius & covers allies) ===
  enhancer_shield_amp_t1: enhancer('enhancer_shield_amp_t1', 'Shield Amplifier T1',
    'Expands the native shield field. Capital-class fitting.', 'shield_amplifier', 'LARGE', 1, 1200, 20, {
      shieldRadiusBonus: 30, shieldCoversAllies: true,
    }),
  enhancer_shield_amp_t2: enhancer('enhancer_shield_amp_t2', 'Shield Amplifier T2',
    'Larger shield projection.', 'shield_amplifier', 'LARGE', 2, 4800, 28, {
      shieldRadiusBonus: 55, shieldCoversAllies: true,
    }),
  enhancer_shield_amp_t3: enhancer('enhancer_shield_amp_t3', 'Shield Amplifier T3',
    'Wide-area shield projection.', 'shield_amplifier', 'LARGE', 3, 19000, 36, {
      shieldRadiusBonus: 90, shieldCoversAllies: true,
    }),
  enhancer_shield_amp_t4: enhancer('enhancer_shield_amp_t4', 'Shield Amplifier T4',
    'Fleet umbrella shield.', 'shield_amplifier', 'LARGE', 4, 60000, 45, {
      shieldRadiusBonus: 140, shieldCoversAllies: true,
    }),
};

// ============================================
// UTILITIES (active — hotbar 1-5)
// 3 families × 4 tiers = 12 items
// ============================================

// Removed: warp_disruptor (redundant with beacon), cargo_scanner (merged into sensor_array)
export type UtilityType =
  | 'warp_disruptor'      // kept as enum value for back-compat (no items)
  | 'beacon_deployer'
  | 'cargo_scanner'       // kept as enum value for back-compat (no items)
  | 'fighter_drone_bay'
  | 'miner_drone_bay';

export interface Utility extends EquipmentBase {
  category: 'UTILITY';
  slotType: 'UTILITY';
  utilityType: UtilityType;
  stats: EquipmentModStats;
}

function utility(
  id: string, name: string, description: string,
  utilityType: UtilityType, size: SlotSize, tier: EquipmentTier,
  value: number, volume: number, stats: EquipmentModStats,
): Utility {
  return {
    id, name, description,
    category: 'UTILITY', slotType: 'UTILITY',
    size, tier, value, volume,
    power: powerCost(tier, size),
    utilityType, stats,
  };
}

export const UTILITIES: Record<string, Utility> = {
  // === BEACON DEPLOYER (MEDIUM) ===
  utility_beacon_t1: utility('utility_beacon_t1', 'Beacon Deployer T1',
    'Deploys an interdiction beacon.', 'beacon_deployer', 'MEDIUM', 1, 800, 10, {
      beaconTier: 1, beaconDeployCooldown: 30000,
    }),
  utility_beacon_t2: utility('utility_beacon_t2', 'Beacon Deployer T2',
    'Improved interdiction beacon.', 'beacon_deployer', 'MEDIUM', 2, 3200, 14, {
      beaconTier: 2, beaconDeployCooldown: 25000,
    }),
  utility_beacon_t3: utility('utility_beacon_t3', 'Beacon Deployer T3',
    'Strong interdiction beacon.', 'beacon_deployer', 'MEDIUM', 3, 13000, 18, {
      beaconTier: 3, beaconDeployCooldown: 20000,
    }),
  utility_beacon_t4: utility('utility_beacon_t4', 'Beacon Deployer T4',
    'Capital interdiction beacon.', 'beacon_deployer', 'MEDIUM', 4, 42000, 22, {
      beaconTier: 4, beaconDeployCooldown: 15000,
    }),

  // === FIGHTER DRONE BAY (LARGE) ===
  utility_fighter_bay_t1: utility('utility_fighter_bay_t1', 'Fighter Drone Bay T1',
    'Deploys 2 fighter drones.', 'fighter_drone_bay', 'LARGE', 1, 8000, 30, {
      droneCapacity: 2, droneZoiRadius: 1500,
    }),
  utility_fighter_bay_t2: utility('utility_fighter_bay_t2', 'Fighter Drone Bay T2',
    'Deploys 4 fighter drones.', 'fighter_drone_bay', 'LARGE', 2, 24000, 40, {
      droneCapacity: 4, droneZoiRadius: 2000,
    }),
  utility_fighter_bay_t3: utility('utility_fighter_bay_t3', 'Fighter Drone Bay T3',
    'Deploys 6 fighter drones.', 'fighter_drone_bay', 'LARGE', 3, 64000, 50, {
      droneCapacity: 6, droneZoiRadius: 2500,
    }),
  utility_fighter_bay_t4: utility('utility_fighter_bay_t4', 'Fighter Drone Bay T4',
    'Deploys 8 fighter drones.', 'fighter_drone_bay', 'LARGE', 4, 160000, 60, {
      droneCapacity: 8, droneZoiRadius: 3000,
    }),

  // === MINER DRONE BAY (LARGE) ===
  utility_miner_bay_t1: utility('utility_miner_bay_t1', 'Miner Drone Bay T1',
    'Deploys 2 mining drones.', 'miner_drone_bay', 'LARGE', 1, 6000, 28, {
      droneCapacity: 2, droneZoiRadius: 1500,
    }),
  utility_miner_bay_t2: utility('utility_miner_bay_t2', 'Miner Drone Bay T2',
    'Deploys 4 mining drones.', 'miner_drone_bay', 'LARGE', 2, 18000, 38, {
      droneCapacity: 4, droneZoiRadius: 2000,
    }),
  utility_miner_bay_t3: utility('utility_miner_bay_t3', 'Miner Drone Bay T3',
    'Deploys 6 mining drones.', 'miner_drone_bay', 'LARGE', 3, 50000, 48, {
      droneCapacity: 6, droneZoiRadius: 2500,
    }),
  utility_miner_bay_t4: utility('utility_miner_bay_t4', 'Miner Drone Bay T4',
    'Deploys 8 mining drones.', 'miner_drone_bay', 'LARGE', 4, 120000, 58, {
      droneCapacity: 8, droneZoiRadius: 3000,
    }),
};

// ============================================
// REGISTRY + HELPERS
// ============================================
export type Equipment = Engine | Weapon | MiningTool | Shield | Enhancer | Utility;

export const ALL_EQUIPMENT: Record<string, Equipment> = {
  ...ENGINES,
  ...WEAPONS,
  ...MINING_TOOLS,
  ...SHIELDS,
  ...ENHANCERS,
  ...UTILITIES,
};

// ============================================
// LEGACY ID ALIASES
// Maps removed/renamed item IDs to current ones. Used by getEquipmentById so
// existing DB rows (ship_slots / ship_inventory) and seed data keep working
// without a destructive migration.
// ============================================
export const LEGACY_EQUIPMENT_ALIASES: Record<string, string> = {
  // Old kinetic variants (autocannon / railgun) → unified kinetic
  weapon_kinetic_t1: 'weapon_kinetic_t1',
  weapon_kinetic_t2: 'weapon_kinetic_t2',
  weapon_kinetic_t3: 'weapon_kinetic_t3',
  weapon_kinetic_t4: 'weapon_kinetic_t4',
  // Old laser variants → unified energy
  weapon_laser_t1: 'weapon_energy_t1',
  weapon_laser_t2: 'weapon_energy_t2',
  weapon_laser_t3: 'weapon_energy_t3',
  weapon_laser_t4: 'weapon_energy_t4',
  // Old missile/torpedo variants → unified explosive
  weapon_missile_t1: 'weapon_explosive_t1',
  weapon_missile_t2: 'weapon_explosive_t2',
  weapon_missile_t3: 'weapon_explosive_t3',
  weapon_missile_t4: 'weapon_explosive_t4',
  // Old mining tool variants → unified mining tool
  tool_laser_t1: 'tool_mining_t1',
  tool_laser_t2: 'tool_mining_t2',
  tool_laser_t3: 'tool_mining_t3',
  tool_laser_t4: 'tool_mining_t4',
  tool_plasma_t1: 'tool_mining_t1',
  tool_plasma_t2: 'tool_mining_t2',
  tool_plasma_t3: 'tool_mining_t3',
  tool_plasma_t4: 'tool_mining_t4',
  tool_sonic_t1: 'tool_mining_t1',
  tool_sonic_t2: 'tool_mining_t2',
  tool_sonic_t3: 'tool_mining_t3',
  tool_sonic_t4: 'tool_mining_t4',
  // Old enhancer IDs (utility_* prefix) → new enhancer_* prefix
  utility_hull_t1: 'enhancer_hull_t1',
  utility_hull_t2: 'enhancer_hull_t2',
  utility_hull_t3: 'enhancer_hull_t3',
  utility_hull_t4: 'enhancer_hull_t4',
  utility_cargo_t1: 'enhancer_cargo_t1',
  utility_cargo_t2: 'enhancer_cargo_t2',
  utility_cargo_t3: 'enhancer_cargo_t3',
  utility_cargo_t4: 'enhancer_cargo_t4',
  utility_sensor_t1: 'enhancer_sensor_t1',
  utility_sensor_t2: 'enhancer_sensor_t2',
  utility_sensor_t3: 'enhancer_sensor_t3',
  utility_sensor_t4: 'enhancer_sensor_t4',
  utility_booster_t1: 'enhancer_booster_t1',
  utility_booster_t2: 'enhancer_booster_t2',
  utility_booster_t3: 'enhancer_booster_t3',
  utility_booster_t4: 'enhancer_booster_t4',
};

export function getEquipmentById(id: string): Equipment | undefined {
  if (!id) return undefined;
  const direct = ALL_EQUIPMENT[id];
  if (direct) return direct;
  const alias = LEGACY_EQUIPMENT_ALIASES[id];
  if (alias) return ALL_EQUIPMENT[alias];
  return undefined;
}

export function getEquipmentByCategory(category: EquipmentCategory): Equipment[] {
  return Object.values(ALL_EQUIPMENT).filter(eq => eq.category === category);
}

export function getEquipmentByTier(tier: EquipmentTier): Equipment[] {
  return Object.values(ALL_EQUIPMENT).filter(eq => eq.tier === tier);
}

export function getEquipmentBySlotType(slotType: SlotType): Equipment[] {
  return Object.values(ALL_EQUIPMENT).filter(eq => eq.slotType === slotType);
}

// ============================================
// canEquipInSlot
// Validates slot type + size. For full fit-validation (power cap, one-per-type)
// use validateEquip below which takes the full current loadout.
// ============================================
export function canEquipInSlot(equipment: Equipment, slotType: SlotType, slotSize: SlotSize): boolean {
  if (equipment.slotType !== slotType) return false;

  const sizeOrder: SlotSize[] = ['SMALL', 'MEDIUM', 'LARGE'];
  const equipmentSizeIndex = sizeOrder.indexOf(equipment.size);
  const slotSizeIndex = sizeOrder.indexOf(slotSize);
  return equipmentSizeIndex <= slotSizeIndex;
}

// ============================================
// Fit validation against power cap + single-equipment-per-type rule.
// Pass the currently equipped item IDs (excluding the slot being targeted)
// plus the ship's powerCapacity. Returns reason string if invalid.
// ============================================
export interface FitValidationResult {
  ok: boolean;
  reason?: string;
}

function equipmentDiscriminator(eq: Equipment): string {
  switch (eq.category) {
    case 'WEAPON':   return `weapon:${(eq as Weapon).weaponType}`;
    case 'TOOL':     return `tool:${(eq as MiningTool).toolType}`;
    case 'ENHANCER': return `enhancer:${(eq as Enhancer).enhancerType}`;
    case 'UTILITY':  return `utility:${(eq as Utility).utilityType}`;
    case 'ENGINE':   return 'engine';
    case 'SHIELD':   return 'shield';
  }
}

export function validateFit(
  equipment: Equipment,
  slotType: SlotType,
  slotSize: SlotSize,
  otherEquippedIds: string[],
  powerCapacity: number,
): FitValidationResult {
  if (!canEquipInSlot(equipment, slotType, slotSize)) {
    return { ok: false, reason: `Slot mismatch: ${equipment.size} ${equipment.slotType} cannot fit in ${slotSize} ${slotType}` };
  }

  // Sum current power
  let currentPower = 0;
  const discriminator = equipmentDiscriminator(equipment);
  for (const id of otherEquippedIds) {
    const other = getEquipmentById(id);
    if (!other) continue;
    currentPower += other.power;
    if (equipmentDiscriminator(other) === discriminator) {
      return { ok: false, reason: `Only one ${discriminator} can be equipped at a time` };
    }
  }

  if (currentPower + equipment.power > powerCapacity) {
    return { ok: false, reason: `Insufficient power: need ${equipment.power}, only ${powerCapacity - currentPower} available` };
  }

  return { ok: true };
}
